import {Action, handleActions} from 'redux-actions';
import {curry} from 'ramda';
import {MutedState} from '@accurate-player/accurate-player-plugins';
import {
    AUDIO_CHANNEL_DISABLE,
    AUDIO_CHANNEL_ENABLE,
    AUDIO_TRACK_DATA_RESET,
    AUDIO_TRACK_DATA_SET,
    AUDIO_TRACK_ENABLE,
    AUDIO_TRACK_DISABLE,
    AUDIO_TRACKS_DATA_RESET,
    AudioChannelDataPayload,
    AudioTrackDataPayload,
    AudioAvailableChannelOutputPayload,
    AUDIO_AVAILABLE_CHANNEL_OUTPUT_SET,
} from './AudioTracksActions';
import {AudioTrackData} from '../AudioTracksTypes';

export interface AudioTracksState {
    tracks: AudioTrackData[],
    availableChannelOutput?: number;
    onlyMonoAudioAvailable: boolean;
}

export interface AudioChannelMutedState {
    mutedState: MutedState;
    state: AudioTracksState;
    channel: number;
    trackData: AudioTrackData | null;
}

export const setAudioTrack = (state: AudioTracksState, action: Action<AudioTrackDataPayload>): AudioTracksState => {
    const {payload} = action;
    const trackData = payload;
    
    if (trackData)
        return {
            ...state,
            tracks: [
                ...state.tracks,
                trackData,
            ],
        };
    
    return state;
};

const differentTrack = curry((trackData: AudioTrackData, secondTrack: AudioTrackData): boolean => {
    return trackData.src !== secondTrack.src;
});

export const resetAudioTrack = (state: AudioTracksState, action: Action<AudioTrackDataPayload>): AudioTracksState => {
    const {payload} = action;
    const trackData: AudioTrackDataPayload = payload;
    
    if (trackData) {
        const handleFilter = differentTrack(trackData);
        
        return {
            ...state,
            tracks: state.tracks.filter(handleFilter),
        };
    }
    return state;
};

export const resetAudioTracks = (): AudioTracksState => {
    return initialState;
};

export const mapTrackEnable = curry((trackToEnable: AudioTrackData, currentTrack: AudioTrackData) => {
    return {
        ...currentTrack,
        enabled: currentTrack.src === trackToEnable.src,
    };
});

export const setAudioTrackEnable = (state: AudioTracksState, action: Action<AudioTrackDataPayload>): AudioTracksState => {
    const {payload} = action;
    const trackData: AudioTrackDataPayload = payload;
    
    if (trackData) {
        const handleEnable = mapTrackEnable(trackData);
        
        return {
            ...state,
            tracks: state.tracks.map(handleEnable),
        };
    }
    return state;
};

export const mapTrackDisable = curry((trackToDisable: AudioTrackData, currentTrack: AudioTrackData) => {
    if (currentTrack.src === trackToDisable.src) {
        return {
            ...currentTrack,
            enabled: false,
        };
    }
    
    return currentTrack;
});

export const setAudioTrackDisable = (state: AudioTracksState, action: Action<AudioTrackDataPayload>): AudioTracksState => {
    const {payload} = action;
    const trackData: AudioTrackDataPayload = payload;
    
    if (trackData) {
        const handleDisable = mapTrackDisable(trackData);
        
        return {
            ...state,
            tracks: state.tracks.map(handleDisable),
        };
    }
    return state;
};

export const setChannelMutedState = (audioChannelMutedState: AudioChannelMutedState): AudioTracksState => {
    const {
        mutedState,
        state,
        channel,
        trackData,
    } = audioChannelMutedState;
    
    const trackindex = state.tracks.findIndex((track) => track.src === trackData?.src);
    
    if (trackindex !== -1) {
        const channelDataCopy = Array.from(state.tracks[trackindex].channelData);
        const tracksCopy = Array.from(state.tracks);
        
        tracksCopy[trackindex].channelData = channelDataCopy;
        tracksCopy[trackindex].channelData[channel].mutedState = mutedState;
        
        return {
            ...state,
            tracks: tracksCopy,
        };
    }
    return state;
};

export const setAudioChannelEnable = (state: AudioTracksState, action: Action<AudioChannelDataPayload>): AudioTracksState => {
    const {payload} = action;
    const {trackData, channel}: AudioChannelDataPayload = payload;
    
    return setChannelMutedState({
        mutedState: MutedState.Playing,
        state,
        channel,
        trackData,
    });
};

export const setAudioChannelDisable = (state: AudioTracksState, action: Action<AudioChannelDataPayload>): AudioTracksState => {
    const {payload} = action;
    const {trackData, channel}: AudioChannelDataPayload = payload;
    
    return setChannelMutedState({
        mutedState: MutedState.ExplicitMuted,
        state,
        channel,
        trackData,
    });
};

export const setAudioAvailableChannelOutput = (state: AudioTracksState, action: Action<AudioAvailableChannelOutputPayload>): AudioTracksState => {
    const availableChannelOutput = action.payload;
    const onlyMonoAudioAvailable = availableChannelOutput < 2;
    
    return {
        ...state,
        availableChannelOutput,
        onlyMonoAudioAvailable,
    };
};

export const initialState: AudioTracksState = {
    tracks: [],
    onlyMonoAudioAvailable: false,
};

const handlers = {
    [AUDIO_TRACK_DATA_SET]: setAudioTrack,
    [AUDIO_TRACK_DATA_RESET]: resetAudioTrack,
    [AUDIO_TRACKS_DATA_RESET]: resetAudioTracks,
    [AUDIO_TRACK_ENABLE]: setAudioTrackEnable,
    [AUDIO_TRACK_DISABLE]: setAudioTrackDisable,
    [AUDIO_CHANNEL_ENABLE]: setAudioChannelEnable,
    [AUDIO_CHANNEL_DISABLE]: setAudioChannelDisable,
    [AUDIO_AVAILABLE_CHANNEL_OUTPUT_SET]: setAudioAvailableChannelOutput,
};

export default handleActions<AudioTracksState, any>(handlers, initialState);
